import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { authApi, startLogin } from "../lib/auth";
import { errMsg } from "../lib/api";
import { OrderCard } from "../components/admin/OrderCard";
import { RequestCard } from "../components/admin/RequestCard";
import { ProductsManager } from "../components/admin/ProductsManager";
import { AdminStats } from "../components/admin/AdminStats";
import { MonthlySales } from "../components/admin/MonthlySales";

const Login = ({ error }) => (
  <div className="max-w-md mx-auto rounded-[28px] chunk bg-white p-8 text-center" data-testid="admin-login">
    <h1 className="font-display font-extrabold text-3xl">Owner area</h1>
    <p className="mt-3 text-[#3b3550]">Sign in with the shop's Google account to see orders and custom requests.</p>
    {error && <p data-testid="admin-login-error" className="mt-4 text-[#b00040] font-semibold">{error}</p>}
    <button data-testid="admin-google-login-btn" onClick={startLogin} className="mt-6 press chunk-sm rounded-full bg-[var(--pink)] text-white font-bold px-6 py-3">
      Sign in with Google
    </button>
  </div>
);

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user ?? null);
  const [checked, setChecked] = useState(!!location.state?.user);
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) return;
    authApi.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null)).finally(() => setChecked(true));
  }, [user]);

  const load = useCallback(async () => {
    try {
      const [o, r] = await Promise.all([authApi.get("/admin/orders"), authApi.get("/admin/custom-requests")]);
      setOrders(Array.isArray(o.data) ? o.data : []);
      setReqs(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const focusRef = new URLSearchParams(location.search).get("order");
  useEffect(() => {
    if (!focusRef || !orders.length) return;
    const t = setTimeout(() => document.getElementById(`order-${focusRef}`)?.scrollIntoView({ block: "center" }), 300);
    return () => clearTimeout(t);
  }, [focusRef, orders.length]);

  const update = async (kind, item, status) => {
    setBusy(true);
    try {
      await authApi.patch(`/admin/${kind}/${item.id}`, { status });
      toast.success(kind === "orders" && status === "paid" ? "Marked paid - customer emailed" : "Status updated");
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const recordPayment = async (o, amount) => {
    setBusy(true);
    try {
      const r = await authApi.post(`/admin/orders/${o.id}/payments`, { amount });
      toast.success(r.data?.status === "paid" ? "Paid in full - customer emailed that printing has started" : "Payment recorded - customer emailed");
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteOrder = async (o) => {
    setBusy(true);
    try {
      await authApi.delete(`/admin/orders/${o.id}`);
      toast.success(`Order ${o.reference} deleted`);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelOrder = async (o) => {
    setBusy(true);
    try {
      await authApi.post(`/admin/orders/${o.id}/cancel`);
      toast.success(`Order ${o.reference} cancelled - customer emailed`);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await authApi.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const awaiting = orders.filter((o) => o.status === "awaiting_payment").length;

  return (
    <main className="px-4 sm:px-8 pt-28 sm:pt-36 pb-24 min-h-screen layer-lines" data-testid="admin-page">
      <div className="max-w-5xl mx-auto">
        {!checked ? <p className="font-mono-x">Checking...</p> : !user ? <Login error={location.state?.error} /> : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <p className="font-mono-x text-xs uppercase tracking-[0.2em] font-bold">Signed in as {user.email}</p>
                <h1 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl mt-2">Orders, requests & items</h1>
              </div>
              <button data-testid="admin-logout-btn" onClick={logout} className="press chunk-sm rounded-full bg-white px-4 py-2 font-semibold flex items-center gap-2">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
            <AdminStats orders={orders} requests={reqs} />
            <MonthlySales orders={orders} />
            <div className="flex flex-wrap gap-2 mb-6">
              {[["orders", `Orders (${orders.length})`], ["requests", `Custom requests (${reqs.length})`], ["products", "Shop items"]].map(([k, l]) => (
                <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
                  className={`px-4 py-2 rounded-full border-2 border-[var(--ink)] font-semibold transition-colors ${tab === k ? "bg-[var(--ink)] text-[var(--paper)]" : "bg-white"}`}>{l}</button>
              ))}
              {awaiting > 0 && <span data-testid="admin-awaiting-count" className="px-4 py-2 rounded-full bg-[var(--yellow)] border-2 border-[var(--ink)] font-semibold">{awaiting} awaiting payment</span>}
            </div>
            <div className="space-y-5">
              {tab === "orders" && (orders.length ? orders.map((o) => <OrderCard key={o.id} o={o} busy={busy} highlight={o.reference === focusRef} onCancel={cancelOrder} onDelete={deleteOrder} onPayment={recordPayment} onStatus={(x, s) => update("orders", x, s)} />)
                : <p data-testid="admin-orders-empty" className="font-display text-xl">No orders yet.</p>)}
              {tab === "requests" && (reqs.length ? reqs.map((r) => <RequestCard key={r.id} r={r} busy={busy} onStatus={(x, s) => update("custom-requests", x, s)} />)
                : <p data-testid="admin-requests-empty" className="font-display text-xl">No custom requests yet.</p>)}
              {tab === "products" && <ProductsManager />}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
